import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import React, { memo } from 'react';

import { neutral } from '../../../styles/mui/colors';
import useScreenSize from '../../../util/hooks/useScreenSize';
import useStoryblokImage from '../../../util/hooks/useStoryblokImage';
import ProgressiveImage from '../../Image/ProgressiveImage';
import Link from '../../Links/Link/Link';

const linkStyle = {
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'none',
  },
};

const cardStyle = {
  display: 'flex',
  width: '100%',
  p: 1,
  boxShadow: 0,
  border: `1px solid ${neutral[200]}`,
  borderRadius: 2,
  '&:hover': {
    boxShadow: 1,
  },
};

const cardMediaStyle = {
  width: { xs: 110, md: 175 },
  height: { xs: 96, md: 120 },
  borderRadius: 1,
  overflow: 'hidden',
};

const cardContentStyle = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  '&:last-child': { pl: 2 },
};

/**
 * RegionCardComponent renders a card that displays information about a specific region,
 * including an image, title, and an optional subtitle. The card is clickable and links
 * to a specified destination URL.
 *
 * @component
 *
 * @param {object} props - The props object.
 * @param {string} props.title - The title of the region.
 * @param {string} [props.subtitle] - The subtitle or additional description of the region (optional).
 * @param {object} props.image - The image object containing details about the region's image.
 * @param {string} props.link - The URL to navigate to when the card is clicked.
 *
 * @returns {JSX.Element} A card component that displays the region details and links to the region page.
 */
const RegionCardComponent = ({ title, subtitle = '', image, link }) => {
  const { filename, alt, title: imageTitle } = image;
  const { sources, src, blurSrc } = useStoryblokImage(filename, {
    size: 320,
  });

  const { isMobile } = useScreenSize();

  return (
    <Link sx={linkStyle} to={link} underline="none" width="100%">
      <Card sx={cardStyle}>
        <CardMedia sx={cardMediaStyle}>
          <ProgressiveImage
            alt={alt}
            blurSrc={blurSrc}
            height={isMobile ? '96px' : '120px'}
            sources={sources}
            src={src}
            title={imageTitle}
          />
        </CardMedia>
        <CardContent sx={cardContentStyle}>
          <Typography component="h3" variant="h5">
            {title}
          </Typography>
          <Typography color="text.secondary" component="p" variant="subtitle1">
            {subtitle}
          </Typography>
        </CardContent>
      </Card>
    </Link>
  );
};

RegionCardComponent.displayName = 'RegionCard';
const RegionCard = memo(RegionCardComponent);
export default RegionCard;
